import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "@/utils/Functions";
import { ModeEditOutline, Close, Lock, LockOpen } from '@mui/icons-material';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select";
import { RichTextEditor } from "./RichTextEditor"; 
import { type ContractSection } from "../Variables/variables.config";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { Button } from "@/components/ui/Button";

type SectionMode = 'base' | 'edit' | 'rich';

interface SectionCardProps {
  section: ContractSection;
  variables: Record<string, string>;
  onVariableChange: (key: string, value: string) => void;
}

// 
function renderPlainText(parts: Array<string | { var: string }>, vars: Record<string, string>) {
  return parts.map((part) => (typeof part === 'string' ? part : vars[part.var])).join('');
}

function renderHighlightedText(parts: Array<string | { var: string }>, vars: Record<string, string>, sectionId: string) {
  return parts.map((part, idx) => {
    if (typeof part === 'string') {
      return <span key={`txt-${sectionId}-${idx}`}>{part}</span>;
    }
    const key = part.var;
    return (
      <span 
        key={`var-${sectionId}-${key}-${idx}`}
        className="inline-block text-white/90 "
      >
        {vars[key]}
      </span>
    );
  });
}

export function SectionCard({ 
  section, 
  variables, 
  onVariableChange
}: SectionCardProps) {
  const [sectionMode, setSectionMode] = useState<SectionMode>('base');
  const [showUnlockPopover, setShowUnlockPopover] = useState(false);

  const handleConfirmEdit = () => {
    toast.success('switched to rich text mode', {
      description: 'You can now edit the text using the rich text editor',
      icon: <LockOpen className="size-4 text-yellow-600" />,
      position: 'bottom-center',
    });
    setSectionMode('rich');
    setShowUnlockPopover(false);
  };

  const handleCancelUnlock = () => {
    setShowUnlockPopover(false);
  };

  const handleCancelEdit = () => {
    setSectionMode('base');
  };

  return (
    <div className={cn("p-6 bg-neutral-800 rounded-2xl group", {
      "shadow-[inset_0_0_0_2px_rgba(255,255,255,0.1)]": sectionMode === 'edit' || sectionMode === 'rich',
    })}>
      {/* card header */}
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-bold mb-2">{section.title}</h4>
        {/* actions */}
        <AnimatePresence mode="wait">
          {sectionMode === 'edit' ? (
            <motion.div 
              key="edit-actions"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="flex items-center gap-2 group-hover:opacity-100 opacity-0 transition-opacity"
            >
              <button 
                aria-label="Cancel" 
                className="w-10 h-10 grid place-items-center hover:bg-red-500/20 rounded-full transition-colors"
                onClick={handleCancelEdit}
              >
                <Close className="size-4 text-red-400" />
              </button>
              <Popover open={showUnlockPopover} onOpenChange={setShowUnlockPopover}>
                <PopoverTrigger render={() => (
                  <button
                    aria-label="Unlock rich text mode" 
                    className="w-10 h-10 grid place-items-center hover:bg-yellow-500/20 rounded-full transition-colors"
                    onClick={() => setShowUnlockPopover(true)}
                  >
                    <Lock className="size-4 text-yellow-400" />
                  </button>
                )} />
                <PopoverContent 
                  side="top" 
                  align="center"
                  sideOffset={8}
                  className="w-auto p-4 text-sm bg-neutral-900/95"
                >
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <LockOpen className="size-3 text-yellow-600" />
                      <span className="font-medium text-lg">Are you sure you want to unlock rich text mode?</span>
                    </div>
                    <div className="flex items-center gap-2 w-full">
                      <Button
                        variant="outline"
                        onClick={handleCancelUnlock}
                        className="w-full rounded-full"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="default"
                        onClick={handleConfirmEdit}
                        className="w-full rounded-full"
                      >
                        Unlock
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </motion.div>
          ) : (
            <motion.div 
              key="default-actions"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="flex items-center gap-2"
            >
              <button 
                aria-label="Edit" 
                className="w-10 h-10 grid place-items-center hover:bg-white/10 rounded-lg group-hover:opacity-100 opacity-100 md:opacity-0 transition-opacity" 
                onClick={() => setSectionMode('edit')}
              >
                <ModeEditOutline className="size-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* card body */}
      <div className="mt-2">
        {sectionMode === 'base' && (
          <p className="text-md text-neutral-400 leading-7">
            {renderHighlightedText(section.templateParts, variables, section.id)}
          </p>
        )}

        {/* edit text */}
        {sectionMode === 'edit' && (
          <>
            <p className="text-md text-neutral-400 leading-7">
              {section.templateParts.map((part, idx) => {
                if (typeof part === 'string') return <span key={`txt-${section.id}-${idx}`}>{part}</span>;
                const key = part.var;
                return (
                  <Select
                    key={`select-${section.id}-${key}-${idx}`}
                    value={variables[key]}
                    onValueChange={(v) => onVariableChange(key, v ?? '')}
                  >
                    <SelectTrigger className="inline-flex h-auto w-auto px-0 py-0 border-none rounded-none bg-transparent shadow-none text-neutral-100 hover:text-neutral-300 underline focus:outline-none [&_svg]:hidden">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {section.variableOptions[key]?.map((opt) => (
                        <SelectItem className="pr-6" key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })}
            </p>
          </>
        )}

        {/* rich text editor */}
        {sectionMode === 'rich' && (
          <RichTextEditor 
            content={renderPlainText(section.templateParts, variables)}
            templateParts={section.templateParts}
            variables={variables}
            onVariableChange={onVariableChange}
          />
        )}
      </div>
    </div>
  );
};
